import React from 'react';
import { Box, Grid, Paper, Typography, Chip, Button, CardMedia, alpha } from '@mui/material';
import {
  Publish as PublishIcon,
  CheckCircle as ApproveIcon,
  Rocket as ReleaseIcon,
  Archive as ArchiveIcon,
  School as GradeLevelIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { categories } from '../../constants/categories';

const SummaryCard = ({ 
  content, 
  readingCategories = [],
  onPublish, 
  onApprove,
  onRelease,
  onArchive, 
  isFromDraftTab,
  isFromPublishedTab, 
  isFromApprovedTab,
  isFromReleasedTab,
  isFromArchivedTab 
}) => {
  const theme = useTheme();

  const imageUrl = content.cover.thumbnailUrl;

  // Get grade level information, if available
  const gradeLevel = content.cover?.gradeLevel ||
    content.gradeLevel ||
    content.metadata?.gradeLevel;

  // Determine the content's current status based on which tab it's from
  // Content in a tab means it has that status (e.g., content in draft tab has DRAFT status)
  const contentStatus = isFromDraftTab ? 'DRAFT' : 
                       isFromPublishedTab ? 'PUBLISHED' : 
                       isFromApprovedTab ? 'APPROVED' : 
                       isFromReleasedTab ? 'RELEASED' : 
                       isFromArchivedTab ? 'ARCHIVED' : 'DRAFT';

  // Function to get the current status chip
  const getStatusChip = () => {
    const statusConfig = {
      'DRAFT': { label: 'Draft', color: 'default', bgColor: theme.palette.grey[500] },
      'PUBLISHED': { label: 'Published', color: 'success', bgColor: theme.palette.success.main },
      'APPROVED': { label: 'Approved', color: 'info', bgColor: theme.palette.info.main },
      'RELEASED': { label: 'Released', color: 'primary', bgColor: theme.palette.primary.main },
      'ARCHIVED': { label: 'Archived', color: 'warning', bgColor: theme.palette.warning.main }
    };

    const config = statusConfig[contentStatus];
    if (!config) return null;

    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        sx={{
          bgcolor: alpha(config.bgColor, 0.1),
          borderColor: config.bgColor,
          color: config.color === 'default' ? theme.palette.grey[700] : config.bgColor,
          fontWeight: 500,
          '& .MuiChip-icon': {
            color: config.color === 'default' ? theme.palette.grey[700] : config.bgColor
          }
        }}
      />
    );
  };

  return (
    <Paper
      sx={{
        mb: 4,
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: theme.palette.background.paper,
        boxShadow: 'none',
        border: '1px solid',
        borderColor: alpha(theme.palette.common.white, 0.1)
      }}
    >
      <Grid container>
        {/* Left Side - Image */}
        <Grid item xs={12} md={4} lg={3}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardMedia
              component="img"
              image={imageUrl}
              alt={content.cover.title}
              sx={{
                height: '100%',
                minHeight: { xs: 250, md: '100%' },
                objectFit: 'cover'
              }}
            />
          </Box>
        </Grid>

        {/* Right Side - Content Details */}
        <Grid item xs={12} md={8} lg={9}>
          <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" gutterBottom color="text.primary">
                Reading Practice Details
              </Typography>
              {getStatusChip()}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, my: 2 }}>
              <Chip
                icon={<CategoryIcon />}
                label={`Category: ${readingCategories?.find(c => c.id === content.cover.categoryId)?.name || categories[content.cover.categoryId] || 'Unknown'}`}
                color="primary"
                variant="outlined"
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  '& .MuiChip-icon': {
                    color: theme.palette.primary.main
                  }
                }}
              />

              {/* 
              <Chip
                icon={<TopicIcon />}
                label={`Topic: ${content.cover.topic}`}
                variant="outlined"
                sx={{
                  bgcolor: alpha(theme.palette.teal?.main || '#00bcd4', 0.15),
                  borderColor: theme.palette.teal?.main || '#00bcd4',
                  color: theme.palette.teal?.main || '#00bcd4',
                  '& .MuiChip-icon': {
                    color: theme.palette.teal?.main || '#00bcd4'
                  }
                }}
              />
              */}

              {gradeLevel && (
                <Chip
                  icon={<GradeLevelIcon />}
                  label={`Grade ${gradeLevel}`}
                  color="info"
                  variant="outlined"
                  sx={{
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    borderColor: theme.palette.info.main,
                    color: theme.palette.info.main,
                    '& .MuiChip-icon': {
                      color: theme.palette.info.main
                    }
                  }}
                />
              )}
            </Box>

            <Typography variant="body1" color="text.secondary" paragraph sx={{ flexGrow: 1 }}>
              {content.cover.synopsis}
            </Typography>

            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              pt: 2,
              borderTop: '1px solid',
              borderColor: alpha(theme.palette.common.white, 0.1)
            }}>
              {/* State transition buttons based on current content status */}
              
              {/* Draft -> Publish */}
              {contentStatus === 'DRAFT' && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PublishIcon />}
                  onClick={onPublish}
                >
                  Publish
                </Button>
              )}

              {/* Published -> Approve */}
              {contentStatus === 'PUBLISHED' && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<ApproveIcon />}
                  onClick={onApprove}
                >
                  Approve
                </Button>
              )}

              {/* Approved -> Release */}
              {contentStatus === 'APPROVED' && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<ReleaseIcon />}
                  onClick={onRelease}
                >
                  Release
                </Button>
              )}

              {/* Archive button - available for all states except archived */}
              {contentStatus !== 'ARCHIVED' && (
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<ArchiveIcon />}
                  onClick={onArchive}
                >
                  Archive
                </Button>
              )}


            </Box>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default SummaryCard;